// A-Statistics — Pure calculation from journal data. Zero AI.

import { MemoryManager } from '../../brain/MemoryManager';
import { NS } from '../../brain/AgentRegistry';

function loadTrades() {
  try { return JSON.parse(localStorage.getItem('jarvis_trades') || '[]'); }
  catch { return []; }
}

function filterByPeriod(trades, period) {
  const now = Date.now();
  switch (period) {
    case 'TODAY': {
      const today = new Date().toDateString();
      return trades.filter(t => new Date(t.date).toDateString() === today);
    }
    case 'WEEK':  return trades.filter(t => now - new Date(t.date).getTime() < 7  * 86400000);
    case 'MONTH': return trades.filter(t => now - new Date(t.date).getTime() < 30 * 86400000);
    default:      return trades;
  }
}

export async function run({ period = 'ALL' } = {}) {
  const all      = loadTrades();
  const trades   = filterByPeriod(all, period);

  if (!trades.length) {
    return { ok: false, error: `No trades for period: ${period}`, stats: null, agent: 'A-Statistics', ts: Date.now() };
  }

  const wins    = trades.filter(t => t.result === 'WIN');
  const losses  = trades.filter(t => t.result === 'LOSS');
  const total   = trades.length;
  const winRate = +(wins.length / total * 100).toFixed(1);
  const totalPnL= +trades.reduce((s, t) => s + (+t.pnl_usd || 0), 0).toFixed(2);

  // Average R:R
  const rrs    = trades.map(t => +t.rr_achieved).filter(r => r > 0);
  const avgRR  = rrs.length ? +(rrs.reduce((a, b) => a + b, 0) / rrs.length).toFixed(2) : 0;

  // Average win / loss
  const avgWin  = wins.length  ? +(wins.reduce((s, t) => s + (+t.pnl_usd||0), 0) / wins.length).toFixed(2)   : 0;
  const avgLoss = losses.length ? +(losses.reduce((s, t) => s + (+t.pnl_usd||0), 0) / losses.length).toFixed(2) : 0;
  const pf      = avgLoss !== 0 ? +(Math.abs(avgWin / avgLoss)).toFixed(2) : 0;

  // Stats by session
  const bySession = {};
  trades.forEach(t => {
    const s = t.session || 'Unknown';
    if (!bySession[s]) bySession[s] = { total: 0, wins: 0, pnl: 0 };
    bySession[s].total++;
    if (t.result === 'WIN') bySession[s].wins++;
    bySession[s].pnl += +t.pnl_usd || 0;
  });
  const sessionStats = Object.entries(bySession).map(([session, d]) => ({
    session,
    total:   d.total,
    wins:    d.wins,
    winRate: d.total ? +(d.wins / d.total * 100).toFixed(1) : 0,
    pnl:     +d.pnl.toFixed(2),
  })).sort((a, b) => b.winRate - a.winRate);

  const bestSession = sessionStats[0]?.session || '—';

  // Stats by setup type
  const bySetup = {};
  trades.forEach(t => {
    const s = t.setup_type || 'Unknown';
    if (!bySetup[s]) bySetup[s] = { total: 0, wins: 0 };
    bySetup[s].total++;
    if (t.result === 'WIN') bySetup[s].wins++;
  });

  // Current streak
  let streak = 0, streakType = '';
  for (let i = trades.length - 1; i >= 0; i--) {
    const r = trades[i].result;
    if (!streakType) { streakType = r; streak = 1; }
    else if (r === streakType) streak++;
    else break;
  }

  // Best / worst trade
  const byPnL     = [...trades].sort((a, b) => (+b.pnl_usd||0) - (+a.pnl_usd||0));
  const bestTrade  = byPnL[0]  || null;
  const worstTrade = byPnL[byPnL.length - 1] || null;

  // Today
  const todayTrades = filterByPeriod(trades, 'TODAY');
  const todayPnL = +todayTrades.reduce((s, t) => s + (+t.pnl_usd || 0), 0).toFixed(2);

  const stats = {
    period, total,
    wins: wins.length, losses: losses.length,
    winRate, totalPnL, todayPnL,
    avgRR, avgWin, avgLoss, pf,
    bestSession,
    sessionStats,
    bySetup,
    streak: `${streak} ${streakType || '—'}`,
    bestTrade, worstTrade,
    recommendation: winRate >= 60
      ? `Strong: ${winRate}% WR, P&L ${totalPnL >= 0 ? '+' : ''}$${totalPnL}`
      : winRate >= 50
      ? `Acceptable: ${winRate}% WR. Focus on ${sessionStats[sessionStats.length-1]?.session || 'weak session'}.`
      : `Review needed: ${winRate}% WR. Pause and analyze losing setups.`,
  };

  MemoryManager.write(NS.TRADING, 'last_stats', stats);

  return { ok: true, stats, agent: 'A-Statistics', requiresAI: false, ts: Date.now() };
}
